import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useApi } from 'api/api-context';
import logoSvg from 'assets/logo.svg';
import useLang from 'lang';
import { BOOKING_LOGIN_PATH } from 'routes/paths';
import 'pages/booking/BookingLogin.css';
import 'pages/booking/booking-auth-form.css';
import 'pages/booking/booking.css';

/** Activate a booking guest account from the emailed token. */
export default function BookingActivation() {
  const [params] = useSearchParams();
  const { logoUrl, request } = useApi();
  const { _t } = useLang();
  const [state, setState] = useState<'loading' | 'success' | 'error'>('loading');
  const submittedToken = useRef<string | null>(null);

  useEffect(() => {
    const token = params.get('token');
    if (!token) {
      setState('error');
      return;
    }
    if (submittedToken.current === token) return;
    submittedToken.current = token;
    request({ method: 'POST', url: '/reservations/public/registrations/activate', data: { token } })
      .then(() => setState('success'))
      .catch(() => setState('error'));
  }, [params, request]);

  return (
    <main className="booking-root login-page booking-activation-page" data-testid="booking-activation-page">
      <section
        className="login-card booking-auth-card booking-activation-card"
        aria-labelledby="booking-activation-title"
      >
        <div className="login-logo">
          <img src={logoUrl ?? logoSvg} alt={_t('Logo')} />
        </div>
        <h1 className="login-title" id="booking-activation-title">
          {_t('Booking account activation')}
        </h1>
        {state === 'loading' && (
          <div role="status" className="booking-auth-notice booking-activation-status">
            <p>{_t('Activating...')}</p>
          </div>
        )}
        {state === 'success' && (
          <>
            <div role="status" className="booking-auth-notice booking-auth-notice--success booking-activation-status">
              <p>{_t('Your account is active. You can now log in.')}</p>
            </div>
            <Link className="booking-auth-primary-link" to={BOOKING_LOGIN_PATH.original}>
              {_t('Log in')}
            </Link>
          </>
        )}
        {state === 'error' && (
          <p role="alert" className="booking-auth-error booking-activation-status">
            {_t('This activation link is invalid or expired.')}
          </p>
        )}
      </section>
    </main>
  );
}
